import { Tooltip } from "@/components/generic/Tooltip";
import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";
import { Focusable } from "react-aria";
import { twMerge } from "tailwind-merge";

export interface FocusableTextProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string;
  tooltipDelay?: number;
  disableTooltip?: boolean;
  tooltipText?: string;
  tooltipPlacement?: ComponentProps<typeof Tooltip>["placement"];
}

export function FocusableText({
  text,
  tooltipDelay = 0,
  disableTooltip = false,
  tooltipText,
  tooltipPlacement = "top",
  ...props
}: FocusableTextProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Check if text is truncated on mount and when element resizes
  useEffect(() => {
    const checkTruncation = () => {
      if (ref.current) {
        // Add a small tolerance for height differences (3px)
        const heightTolerance = 3;
        const isOverflowing =
          ref.current.scrollWidth > ref.current.clientWidth ||
          ref.current.scrollHeight - ref.current.clientHeight > heightTolerance;
        setIsTruncated(isOverflowing);
      }
    };

    // Initial check with a small delay to ensure rendering is complete
    setTimeout(checkTruncation, 50);

    // Use ResizeObserver for subsequent checks
    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    // Re-check when the component updates
    checkTruncation();

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [text]);

  // If tooltip is disabled or text isn't truncated, just render the text
  if ((disableTooltip || !isTruncated) && !tooltipText) {
    return (
      <div
        ref={ref}
        tabIndex={0}
        className={
          props.className
            ? props.className
            : twMerge("truncate", props.className)
        }
      >
        {text}
      </div>
    );
  }

  // Otherwise render with tooltip
  return (
    <Tooltip
      tooltipText={tooltipText ?? text}
      tooltipTriggerProps={{ delay: tooltipDelay }}
      placement={tooltipPlacement}
      className="flex flex-col"
    >
      <Focusable>
        <div
          ref={ref}
          role="button" // According to ChatGPT, "button" is the most appropriate role for a focusable text element that triggers a tooltip
          tabIndex={0}
          className={props.className || "truncate"}
        >
          {text}
        </div>
      </Focusable>
    </Tooltip>
  );
}
